import type { BaseGroupRoute, BaseLinkRoute, BaseRoute } from '@connected/ui/features/navigation';

import {

    createNavigationContext,
    isGroupRoute,
    isLinkRoute,
} from '@connected/ui/features/navigation';
import Calendar from '@connected/ui/icons/calendar';
import ClipboardPlus from '@connected/ui/icons/clipboard-plus';
import File from '@connected/ui/icons/file';
import FileCheck from '@connected/ui/icons/file-check';
import FileQuestion from '@connected/ui/icons/file-question';
import FileSearch from '@connected/ui/icons/file-search';
import Flag from '@connected/ui/icons/flag';
import Image from '@connected/ui/icons/image';
import InfoSquare from '@connected/ui/icons/info-square';
import Users from '@connected/ui/icons/users';
import { Building, ChartSpline, Wrench, Zap } from 'lucide-react';

import type { ElektraConditionContext, ElektraConditionName } from '@/config/conditions';

import { WrenchScrewdriver } from '@/components/tools-icon';

// ============================================================================
// Types
// ============================================================================

/**
 * Context available for evaluating route visibility conditions.
 * Now uses ElektraConditionContext from the unified condition system.
 */
export type NavigationContext = ElektraConditionContext;

/** App-specific type aliases using generic base types from @connected/ui */
export type LinkRoute = BaseLinkRoute<NavigationContext, ElektraConditionName>;
export type GroupRoute = BaseGroupRoute<NavigationContext, ElektraConditionName>;
export type Route = BaseRoute<NavigationContext, ElektraConditionName>;

// Re-export type guards for convenience (they work with any context type)
export { isGroupRoute, isLinkRoute };

// ============================================================================
// Navigation Configuration
// ============================================================================

export const navigationRoutes: Route[] = [
    {
        name: 'group.request',
        routes: [
            {
                name: 'sidebar.request-concerning',
                path: '/projects/$projectVersionId/applications/request-concerning',
                icon: FileQuestion,
            },
            {
                name: 'sidebar.object',
                path: '/projects/$projectVersionId/applications/objects',
                icon: Building,
            },
            {
                name: 'sidebar.those-involved',
                path: '/projects/$projectVersionId/applications/those-involved/client',
                icon: Users,
                routes: [
                    {
                        name: 'tabs.those-involved.client',
                        path: '/projects/$projectVersionId/applications/those-involved/client',
                    },
                    {
                        name: 'tabs.those-involved.owner',
                        path: '/projects/$projectVersionId/applications/those-involved/owner',
                    },
                    {
                        name: 'tabs.those-involved.managers',
                        path: '/projects/$projectVersionId/applications/those-involved/managers',
                    },
                    {
                        name: 'tabs.those-involved.users',
                        path: '/projects/$projectVersionId/applications/those-involved/users',
                    },
                ],
            },
            {
                name: 'sidebar.drawings',
                path: '/projects/$projectVersionId/applications/drawings',
                icon: Image,
            },
            {
                name: 'sidebar.recap',
                path: '/projects/$projectVersionId/applications/recap',
                icon: File,
            },
        ],
    },
    {
        name: 'group.quotation',
        condition: 'canAccessQuotation',
        routes: [
            {
                name: 'sidebar.quotes',
                path: '/projects/$projectVersionId/quotation',
                icon: ClipboardPlus,
            },
        ],
    },
    {
        name: 'group.inspection-plans',
        routes: [
            {
                name: 'sidebar.inspection-plans',
                path: '/projects/$projectVersionId/inspection-plans/information',
                icon: FileSearch,
                routes: [
                    {
                        name: 'tabs.inspection-plans.information',
                        path: '/projects/$projectVersionId/inspection-plans/information',
                    },
                    {
                        name: 'tabs.inspection-plans.plan-of-approach',
                        path: '/projects/$projectVersionId/inspection-plans/plan-of-approach',
                    },
                ],
            },
        ],
    },
    {
        name: 'group.schedule',
        condition: 'canAccessSchedule',
        routes: [
            {
                name: 'sidebar.schedules',
                path: '/projects/$projectVersionId/schedules',
                icon: Calendar,
            },
        ],
    },
    {
        name: 'group.inspection',
        routes: [
            {
                name: 'sidebar.inspection-information',
                path: '/projects/$projectVersionId/re-inspections/information',
                icon: InfoSquare,
            },
            {
                name: 'sidebar.distribution-boards',
                path: '/projects/$projectVersionId/re-inspections/distributionboards',
                icon: WrenchScrewdriver,
                routes: [
                    {
                        name: 'tabs.distribution-board.information',
                        path: '/projects/$projectVersionId/re-inspections/distributionboards/$distributionboardId/board/information',
                    },
                    {
                        name: 'tabs.distribution-board.checklist',
                        path: '/projects/$projectVersionId/re-inspections/distributionboards/$distributionboardId/board/checklist',
                    },
                    {
                        name: 'tabs.distribution-board.measurements',
                        path: '/projects/$projectVersionId/re-inspections/distributionboards/$distributionboardId/board/measurements',
                    },
                    {
                        name: 'tabs.distribution-board.residual-current-devices',
                        path: '/projects/$projectVersionId/re-inspections/distributionboards/$distributionboardId/board/residual-current-devices',
                    },
                    {
                        name: 'tabs.distribution-board.residual-current-circuit-breakers',
                        path: '/projects/$projectVersionId/re-inspections/distributionboards/$distributionboardId/board/residual-current-circuit-breakers',
                        condition: 'canAccessResidualCurrentCircuitBreakers',
                    },
                    {
                        name: 'tabs.distribution-board.final-circuit-protective-devices',
                        path: '/projects/$projectVersionId/re-inspections/distributionboards/$distributionboardId/board/final-circuit-protective-devices',
                    },
                    {
                        name: 'tabs.distribution-board.riso',
                        path: '/projects/$projectVersionId/re-inspections/distributionboards/$distributionboardId/board/riso',
                        condition: 'canAccessExtendedRiso',
                    },
                    {
                        name: 'tabs.distribution-board.impedance-measurements',
                        path: '/projects/$projectVersionId/re-inspections/distributionboards/$distributionboardId/board/impedance-measurements',
                        condition: 'canAccessImpedanceMeasurementsPage',
                    },
                ],
            },
            {
                name: 'sidebar.final-circuits',
                path: '/projects/$projectVersionId/re-inspections/final-circuits',
                icon: Wrench,
                routes: [
                    {
                        name: 'tabs.final-circuit.information',
                        path: '/projects/$projectVersionId/re-inspections/final-circuits/$finalCircuitId/information',
                    },
                    {
                        name: 'tabs.final-circuit.checklist',
                        path: '/projects/$projectVersionId/re-inspections/final-circuits/$finalCircuitId/checklist',
                    },
                ],
            },
        ],
    },
    {
        name: 'group.solar-inspection',
        condition: 'canAccessSolarInspection',
        routes: [
            {
                name: 'sidebar.pv-inverters',
                path: '/projects/$projectVersionId/re-inspections/pv-inverters',
                icon: Zap,
            },
            {
                name: 'sidebar.control-points',
                path: '/projects/$projectVersionId/re-inspections/controlpoints',
                icon: ChartSpline,
            },
        ],
    },
    {
        name: 'group.inspection-summary',
        routes: [
            {
                name: 'sidebar.completion',
                path: '/projects/$projectVersionId/re-inspections/completion',
                icon: Flag,
            },
            {
                name: 'sidebar.summary',
                path: '/projects/$projectVersionId/re-inspections/summary',
                icon: FileCheck,
            },
        ],
    },
];

// ============================================================================
// React Context (using generic factory from @connected/ui)
// ============================================================================

export const { NavigationProvider, useNavigationContext }
    = createNavigationContext<NavigationContext>();
